import React from "react";
import { useParams, Link } from "react-router-dom";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { ExternalLink, ArrowLeft, Bookmark, AlertTriangle } from "lucide-react";
import api from "@/lib/api";
import ScoreBadge from "@/components/ScoreBadge";
import PriceChart from "@/components/PriceChart";
import DataQualityBadge from "@/components/DataQualityBadge";
import AIReport, { useAIReport } from "@/components/AIReport";
import AIAnalyst from "@/components/AIAnalyst";
import ContrarianView from "@/components/ContrarianView";
import MarketDependencies from "@/components/MarketDependencies";
import InfoTip from "@/components/InfoTip";
import { fmtUSD, fmtPct, fmtNum, truncWallet, timeAgo } from "@/lib/format";
import { Button } from "@/components/ui/button";

export default function MarketDetail() {
  const { id } = useParams();
  const qc = useQueryClient();
  const { data, isLoading, isError } = useQuery({
    queryKey: ["market", id],
    queryFn: async () => (await api.get(`/markets/${id}`)).data,
  });

  const { data: wls } = useQuery({
    queryKey: ["watchlists-market"],
    queryFn: async () => (await api.get("/watchlists")).data,
  });

  const explainMut = useAIReport(`/intel/markets/${id}/explain`, "market");
  const [explanation, setExplanation] = React.useState(null);

  const watchMut = useMutation({
    mutationFn: async () => {
      const wl = (wls || []).find((w) => w.type === "market") || (wls || [])[0];
      if (!wl) return;
      await api.post(`/watchlists/${wl.id}/items/${id}`);
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ["watchlists-market"] }),
  });

  if (isLoading) return <div className="text-sm text-zinc-500">Loading market…</div>;
  if (isError || !data) return <div className="text-sm text-rose-400">Market not found.</div>;

  const m = data.market;
  const consensus = data.consensus;
  const inWatchlist = (wls || []).some((w) => w.item_ids?.includes(id));

  return (
    <div className="space-y-6" data-testid="market-detail">
      <div>
        <Link to="/app/markets" className="text-xs text-zinc-500 hover:text-zinc-200 inline-flex items-center gap-1">
          <ArrowLeft size={12} /> Markets
        </Link>
        <h1 className="font-heading text-2xl font-bold text-zinc-100 mt-2 leading-tight">
          {m.title}
        </h1>
        <div className="flex flex-wrap items-center gap-3 mt-2 text-xs text-zinc-500">
          <span>{m.category || "Uncategorized"}</span>
          <span>·</span>
          <span>Status: <span className="text-zinc-300">{m.status}</span></span>
          {m.end_date && (
            <>
              <span>·</span>
              <span>Ends {new Date(m.end_date).toLocaleDateString()}</span>
            </>
          )}
          {m.is_demo && <DataQualityBadge status="demo" />}
          {m.source_url && (
            <a
              href={m.source_url}
              target="_blank"
              rel="noreferrer"
              className="inline-flex items-center gap-1 text-zinc-400 hover:text-zinc-100"
              data-testid="link-polymarket"
            >
              View on Polymarket <ExternalLink size={11} />
            </a>
          )}
          <Button
            size="sm"
            onClick={() => watchMut.mutate()}
            disabled={inWatchlist}
            data-testid="btn-watchlist-add"
            className="h-7 text-xs bg-zinc-800 hover:bg-zinc-700 text-zinc-100 ml-auto"
          >
            <Bookmark size={11} className="mr-1" /> {inWatchlist ? "Watching" : "Add to watchlist"}
          </Button>
        </div>
      </div>

      {/* Outcome cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-3 sm:gap-4">
        {(m.outcomes || []).map((o, i) => (
          <div key={i} className="border border-zinc-800 rounded-md p-3"
               data-testid={`outcome-${o.name}`}
               title={
                 `Price: ${o.price != null ? (o.price*100).toFixed(1)+'%' : '—'}` +
                 (o.best_bid != null ? `\nBest bid: ${(o.best_bid*100).toFixed(1)}%` : "") +
                 (o.best_ask != null ? `\nBest ask: ${(o.best_ask*100).toFixed(1)}%` : "") +
                 (o.spread != null ? `\nSpread: ${(o.spread*100).toFixed(2)}pp` : "")
               }>
            <div className="text-[10px] uppercase tracking-widest text-zinc-500">{o.name}</div>
            <div className="text-xl font-mono-num text-zinc-100 mt-1">
              {o.price != null ? fmtPct(o.price) : "—"}
            </div>
            {(o.best_bid != null || o.best_ask != null) && (
              <div className="mt-1 text-[10px] text-zinc-500 font-mono-num">
                bid {o.best_bid != null ? (o.best_bid*100).toFixed(1)+'%' : '—'}
                {" / "}
                ask {o.best_ask != null ? (o.best_ask*100).toFixed(1)+'%' : '—'}
              </div>
            )}
            {o.spread != null && (
              <div className="text-[10px] text-zinc-500 font-mono-num">
                spread {(o.spread * 100).toFixed(2)}pp
              </div>
            )}
          </div>
        ))}
        <div className="border border-zinc-800 rounded-md p-3">
          <div className="text-[10px] uppercase tracking-widest text-zinc-500 flex items-center gap-1">
            <span>Volume</span>
            <InfoTip content="Total volume traded on this market." testid="tip-market-volume" />
          </div>
          <div className="text-xl font-mono-num text-zinc-100 mt-1">{fmtUSD(m.volume)}</div>
        </div>
        <div className="border border-zinc-800 rounded-md p-3">
          <div className="text-[10px] uppercase tracking-widest text-zinc-500 flex items-center gap-1">
            <span>Liquidity</span>
            <InfoTip content="Depth of the order book — how much can be traded before price moves significantly." testid="tip-market-liquidity" />
          </div>
          <div className="text-xl font-mono-num text-zinc-100 mt-1">{fmtUSD(m.liquidity)}</div>
        </div>
      </div>

      {/* Price chart */}
      <section>
        <h2 className="font-heading text-lg font-semibold text-zinc-100 mb-2">Probability history</h2>
        <PriceChart history={data.price_history} />
      </section>

      {/* AI Analyst — interactive reasoning */}
      <AIAnalyst endpoint={`/intel/analyst/market/${id}`} testid="market-ai-analyst" />

      {/* Contrarian View */}
      <ContrarianView endpoint={`/intel/markets/${id}/contrarian`} testid="market-contrarian" />

      {/* Market Dependencies */}
      <MarketDependencies marketId={id} testid="market-dependencies" />

      {/* AI Explain This Market */}
      <AIReport
        title="Explain this market"
        report={explanation}
        isGenerating={explainMut.isPending}
        onGenerate={() => explainMut.mutateAsync().then(setExplanation).catch(() => {})}
        ctaLabel="Explain with AI"
        testid="explain-market"
        quotaEndpoint="market_explain"
      />

      {/* Consensus */}
      {consensus && (
        <section className="border border-zinc-800 rounded-md p-4" data-testid="consensus-panel">
          <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between mb-2">
            <h2 className="font-heading text-lg font-semibold text-zinc-100">Consensus</h2>
            <ScoreBadge score={consensus.consensus_score} size="lg" testid="consensus-score" />
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-5 gap-3 text-sm">
            <div>
              <div className="text-[10px] uppercase tracking-widest text-zinc-500 flex items-center gap-1">
                <span>Direction</span>
                <InfoTip content="Direction the majority of tracked traders are betting." testid="tip-consensus-direction" />
              </div>
              <div className="text-zinc-100 mt-1">{consensus.direction || "—"}</div>
            </div>
            <div>
              <div className="text-[10px] uppercase tracking-widest text-zinc-500 flex items-center gap-1">
                <span>Supporting</span>
                <InfoTip content="Number of tracked traders positioned in favor of the consensus direction." testid="tip-consensus-supporting" />
              </div>
              <div className="text-zinc-100 mt-1 font-mono-num">{consensus.supporting_traders}</div>
            </div>
            <div>
              <div className="text-[10px] uppercase tracking-widest text-zinc-500 flex items-center gap-1">
                <span>Opposing</span>
                <InfoTip content="Number of tracked traders positioned against the consensus." testid="tip-consensus-opposing" />
              </div>
              <div className="text-zinc-100 mt-1 font-mono-num">{consensus.opposing_traders}</div>
            </div>
            <div>
              <div className="text-[10px] uppercase tracking-widest text-zinc-500 flex items-center gap-1">
                <span>Supporting exposure</span>
                <InfoTip content="Total capital deployed on the consensus direction across tracked traders." testid="tip-consensus-exposure" />
              </div>
              <div className="text-zinc-100 mt-1 font-mono-num">{fmtUSD(consensus.supporting_exposure)}</div>
            </div>
            <div>
              <div className="text-[10px] uppercase tracking-widest text-zinc-500 flex items-center gap-1">
                <span>Concentration</span>
                <InfoTip content="How lopsided the consensus is. 100% = everyone agrees. 50% = evenly split. High concentration + few traders can mean single-wallet dominance." testid="tip-consensus-concentration" />
              </div>
              <div className="text-zinc-100 mt-1 font-mono-num"
                   data-testid="consensus-concentration">
                {consensus.concentration != null
                  ? `${Math.round(consensus.concentration * 100)}%`
                  : "—"}
              </div>
            </div>
          </div>
          <div className="mt-3 text-xs text-zinc-400">{consensus.explanation}</div>
        </section>
      )}

      {/* Tracked traders */}
      <section>
        <h2 className="font-heading text-lg font-semibold text-zinc-100 mb-2">Tracked traders in this market</h2>
        <div className="border border-zinc-800 rounded-sm overflow-x-auto">
          <table className="w-full text-sm min-w-[800px]">
            <thead className="text-[10px] uppercase tracking-widest text-zinc-500 bg-zinc-900/40">
              <tr>
                <th className="text-left px-3 py-2">Trader</th>
                <th className="text-left px-3 py-2">Direction</th>
                <th className="text-right px-3 py-2">Size</th>
                <th className="text-right px-3 py-2">Avg entry</th>
                <th className="text-right px-3 py-2">Current</th>
                <th className="text-right px-3 py-2">P&amp;L (est.)</th>
                <th className="text-right px-3 py-2">Score</th>
                <th className="text-right px-3 py-2">Last</th>
              </tr>
            </thead>
            <tbody className="stagger-in">
              {data.tracked_traders.length === 0 && (
                <tr><td colSpan={8} className="text-xs text-zinc-500 text-center py-6">
                  No tracked-trader positions detected in this market yet.
                </td></tr>
              )}
              {data.tracked_traders.map((t) => (
                <tr key={t.trader_id} className="border-t border-zinc-800/70 hover:bg-zinc-900/40 transition-colors"
                    data-testid={`tracked-trader-${t.trader_id}`}>
                  <td className="px-3 py-2">
                    <Link to={`/app/traders/${t.trader_id}`} className="text-zinc-100 hover:underline">
                      {t.public_username || truncWallet(t.wallet_address)}
                    </Link>
                    <div className="text-[10px] text-zinc-500 font-mono-num">{truncWallet(t.wallet_address)}</div>
                  </td>
                  <td className="px-3 py-2 text-zinc-300">{t.direction}</td>
                  <td className="px-3 py-2 text-right font-mono-num">{fmtNum(t.size, 2)}</td>
                  <td className="px-3 py-2 text-right font-mono-num">{fmtPct(t.average_price, 1)}</td>
                  <td className="px-3 py-2 text-right font-mono-num">{fmtPct(t.current_price, 1)}</td>
                  <td className={`px-3 py-2 text-right font-mono-num ${
                    (t.cash_pnl || 0) > 0 ? "text-emerald-400" : (t.cash_pnl || 0) < 0 ? "text-rose-400" : "text-zinc-300"
                  }`}>
                    {t.cash_pnl != null ? fmtUSD(t.cash_pnl) : "—"}
                  </td>
                  <td className="px-3 py-2 text-right"><ScoreBadge score={t.trader_score} /></td>
                  <td className="px-3 py-2 text-right text-[11px] text-zinc-500">{timeAgo(t.captured_at)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </section>

      {/* Signals */}
      {data.signals.length > 0 && (
        <section>
          <h2 className="font-heading text-lg font-semibold text-zinc-100 mb-2">Associated signals</h2>
          <div className="border border-zinc-800 rounded-sm divide-y divide-zinc-800/70">
            {data.signals.map((s) => (
              <Link
                key={s.id}
                to={`/app/signals/${s.id}`}
                className="flex flex-col gap-1 sm:flex-row sm:items-center sm:justify-between px-3 py-2 hover:bg-zinc-900/40 transition-colors"
                data-testid={`signal-link-${s.id}`}
              >
                <div>
                  <div className="text-sm text-zinc-100">{(s.signal_type || "signal").replaceAll("_", " ")}</div>
                  <div className="text-[11px] text-zinc-500">{s.actionability_label} · {timeAgo(s.detected_at)}</div>
                </div>
                <ScoreBadge score={s.overall_score} />
              </Link>
            ))}
          </div>
        </section>
      )}

      {/* Resolution */}
      <section>
        <h2 className="font-heading text-lg font-semibold text-zinc-100 mb-2">Resolution</h2>
        <div className="border border-zinc-800 rounded-md p-4 text-sm text-zinc-400">
          {m.resolution_rules ? (
            <p className="leading-relaxed whitespace-pre-wrap">{m.resolution_rules}</p>
          ) : (
            <div className="flex items-start gap-2 text-amber-300">
              <AlertTriangle size={14} className="mt-0.5" />
              Resolution rules unavailable from source. Verify on Polymarket before drawing conclusions.
            </div>
          )}
        </div>
      </section>
    </div>
  );
}
